import axios from 'axios'

const api = axios.create({
  baseURL: '/api',
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' }
})

// Request interceptor - attach UUID if present
api.interceptors.request.use(config => {
  return config
})

// Response interceptor - normalize errors
api.interceptors.response.use(
  res => res,
  err => {
    console.error('[API Error]', err?.response?.data || err.message)
    return Promise.reject(err)
  }
)

export const scheduleAPI = {
  /**
   * Get schedule for a date with pagination
   * @param {string} date - YYYY-MM-DD
   * @param {number} page
   * @param {number} limit
   */
  getSchedule(date, page = 1, limit = 30, uuid = '') {
    return api.get('/schedule', { params: { date, page, limit, uuid, site: 'sg' } })
  },

  /**
   * Search matches by keyword
   * @param {string} q - keyword
   * @param {number} page
   * @param {number} limit
   * @param {string} uuid
   */
  searchMatches(q, page = 1, limit = 30, uuid = '') {
    return api.get('/search', { params: { q, page, limit, uuid, site: 'sg' } })
  }
}

export const oddsAPI = {
  /**
   * Get all odds for a match by analysis_id
   * @param {number} analysisId
   */
  getOdds(analysisId) {
    return api.get(`/odds/${analysisId}`)
  }
}

export const favoritesAPI = {
  /**
   * Get user's favorite matches
   * @param {string} uuid
   * @param {number} page
   * @param {number} limit
   */
  getFavorites(uuid, page = 1, limit = 30) {
    return api.get('/favorites', { params: { uuid, page, limit } })
  },

  /**
   * Get globally popular matches
   * @param {string} uuid - Optional, to get favorite status for current user
   * @param {number} page
   * @param {number} limit
   */
  getPopularFavorites(uuid, page = 1, limit = 30) {
    return api.get('/favorites/popular', { params: { uuid, page, limit, site: 'sg' } })
  },

  /**
   * Add a match to favorites
   * @param {string} uuid
   * @param {number} analysisId
   */
  addFavorite(uuid, analysisId) {
    return api.post('/favorites', { uuid, analysis_id: analysisId, site: 'sg' })
  },

  /**
   * Remove a match from favorites
   * @param {string} uuid
   * @param {number} analysisId
   */
  removeFavorite(uuid, analysisId) {
    return api.delete(`/favorites/${analysisId}`, { params: { uuid, site: 'sg' } })
  }
}

export const betAPI = {
  /**
   * Place/Update mock bets (batch of 3 markets)
   * @param {Object} payload { uuid, analysis_id, selections: [{ market_id, selection_id, odds, line }, ...] }
   */
  placeBet(payload) {
    if (!payload.site) payload.site = 'sg'
    return api.post('/bets', payload)
  },

  /**
   * Get existing bets for a specific match
   * @param {string} uuid
   * @param {number} analysis_id
   */
  getMatchBets(uuid, analysis_id) {
    return api.get(`/bets/match/${analysis_id}`, { params: { uuid } })
  },

  /**
   * Get user betting history
   * @param {string} uuid
   * @param {number} page
   * @param {number} limit
   */
  getBets(uuid, page = 1, limit = 30) {
    return api.get('/bets', { params: { uuid, page, limit, site: 'sg' } })
  }
}

export const rankingAPI = {
  getRanking(uuid) {
    return api.get('/ranking', { params: { uuid, site: 'sg' } })
  }
}

export const followsAPI = {
  toggleFollow(uuid, followedUserId, isFollowing) {
    if (isFollowing) {
      return api.delete('/follows', { data: { uuid, followed_user_id: followedUserId, site: 'sg' } })
    } else {
      return api.post('/follows', { uuid, followed_user_id: followedUserId, site: 'sg' })
    }
  },
  
  getFollows(uuid) {
    return api.get('/follows', { params: { uuid, site: 'sg' } })
  }
}

export default api
